package com.royceecoleman.bill.auth

import kotlinx.browser.window
import kotlinx.coroutines.await
import org.w3c.fetch.RequestCredentials
import org.w3c.fetch.RequestInit
import kotlin.js.unsafeCast

enum class AuthUserRole {
    DEV,
    ADMIN,
    CUSTOMER,
}

data class AuthenticatedSession(
    val id: Int,
    val username: String,
    val role: AuthUserRole,
)

data class CreateUserAccountResult(
    val isSuccess: Boolean,
    val message: String,
    val createdUser: AuthenticatedSession? = null,
)

fun loginActionUrl(): String = "${apiBaseUrl()}/api/auth/login"

suspend fun hasAuthenticatedSession(): Boolean = fetchAuthenticatedSession() != null

suspend fun fetchAuthenticatedSession(): AuthenticatedSession? {
    val response =
        authenticatedFetch(
            path = "/api/auth/session",
            method = "GET",
        )

    if (!response.ok) {
        return null
    }

    return parseAuthenticatedSession(response.text().await())
}

suspend fun createUserAccount(
    username: String,
    password: String,
    role: AuthUserRole,
): CreateUserAccountResult =
    submitCreateUserAccountRequest(
        path = "/api/users",
        username = username,
        password = password,
        role = role,
    )

suspend fun registerCustomerAccount(
    username: String,
    password: String,
): CreateUserAccountResult =
    submitCreateUserAccountRequest(
        path = "/api/auth/register",
        username = username,
        password = password,
        role = AuthUserRole.CUSTOMER,
    )

fun AuthUserRole.canAccessAdminDashboard(): Boolean = this == AuthUserRole.DEV || this == AuthUserRole.ADMIN

// Keep this in sync with the server-side create-user contract in Auth.kt.
fun AuthUserRole.creatableRoles(): List<AuthUserRole> =
    when (this) {
        AuthUserRole.DEV -> listOf(AuthUserRole.ADMIN, AuthUserRole.CUSTOMER)
        AuthUserRole.ADMIN -> listOf(AuthUserRole.CUSTOMER)
        AuthUserRole.CUSTOMER -> emptyList()
    }

fun AuthUserRole.displayName(): String =
    when (this) {
        AuthUserRole.DEV -> "Dev"
        AuthUserRole.ADMIN -> "Admin"
        AuthUserRole.CUSTOMER -> "Customer"
    }

internal suspend fun authenticatedFetch(
    path: String,
    method: String,
    body: String? = null,
    contentType: String? = null,
) = window
    .fetch(
        input = "${apiBaseUrl()}$path",
        init =
            RequestInit(
                method = method,
                credentials = "include".unsafeCast<RequestCredentials>(),
            ).apply {
                if (body != null) {
                    this.body = body
                }
                if (contentType != null) {
                    this.headers = jsonHeaders(contentType)
                }
                if (path.startsWith("/api/workbench")) {
                    val protectedHeaders = jsonHeaders(contentType ?: "application/json")
                    protectedHeaders["X-Bill-Request"] = "workbench"
                    this.headers = protectedHeaders
                }
            },
    ).await()

private suspend fun submitCreateUserAccountRequest(
    path: String,
    username: String,
    password: String,
    role: AuthUserRole,
): CreateUserAccountResult {
    val response =
        authenticatedFetch(
            path = path,
            method = "POST",
            body = createUserRequestJson(username, password, role),
            contentType = "application/json",
        )
    val responseText = response.text().await()

    return if (response.ok) {
        val createdUser =
            parseAuthenticatedSessionOrNull(responseText)
                ?: return CreateUserAccountResult(
                    isSuccess = false,
                    message = "The server returned an unexpected user payload.",
                )

        CreateUserAccountResult(
            isSuccess = true,
            message = "User account created.",
            createdUser = createdUser,
        )
    } else {
        CreateUserAccountResult(
            isSuccess = false,
            message = responseText.ifBlank { "Unable to create the user account." },
        )
    }
}

private fun jsonHeaders(contentType: String): dynamic {
    val headers = js("({})")
    headers["Content-Type"] = contentType
    return headers
}

private fun parseAuthenticatedSession(responseText: String): AuthenticatedSession {
    val payload = JSON.parse<dynamic>(responseText)

    return AuthenticatedSession(
        id = (payload.id as Number).toInt(),
        username = payload.username as String,
        role = AuthUserRole.valueOf(payload.role as String),
    )
}

private fun parseAuthenticatedSessionOrNull(responseText: String): AuthenticatedSession? =
    try {
        parseAuthenticatedSession(responseText)
    } catch (_: Throwable) {
        null
    }

private fun createUserRequestJson(
    username: String,
    password: String,
    role: AuthUserRole,
): String {
    val payload = js("({})")
    payload.username = username
    payload.password = password
    payload.role = role.name

    return JSON.stringify(payload)
}

internal fun apiBaseUrl(): String {
    val location = window.location
    val isLocalDevelopmentHost = location.hostname == "localhost" || location.hostname == "127.0.0.1"

    return if (isLocalDevelopmentHost && location.port == "8080") {
        "${location.protocol}//${location.hostname}:8081"
    } else {
        ""
    }
}
